import { publishedAt, modifiedAt, postDateByline } from "@/data/postDates";
import { getPost } from "@/data/posts";
import { ArticleFaqs } from "@/components/site/ArticleFaqs";
import { postFaqs } from "@/data/postFaqs";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookFitCall } from "@/components/site/BookFitCall";
import { articleSchema, faqPageSchema, canonicalLinks, jsonLd, ogImage, ogUrl, breadcrumbSchema, webPageSchema, absoluteUrl } from "@/lib/seo";
import { MarginWaterfall } from "@/components/diagrams/MarginWaterfall";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { PhotoBand } from "@/components/site/PhotoBand";
import priceListMarkupImg from "@/assets/photos/price-list-markup.webp";

const SLUG = "the-6m-price-review";
const TITLE = "The $6M price review";
const DESCRIPTION =
  "One annual price review across a 249-store network produced over $6M in first-year revenue growth. What it says about untested prices in founder-led firms.";
const CATEGORY = "Pricing";
const READ = "4 min read";

export const Route = createFileRoute("/insights/the-6m-price-review")({
  head: () => ({
    meta: [
      { title: `${TITLE} | Ajility` },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: `${TITLE} | Ajility` },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { name: "twitter:title", content: `${TITLE} | Ajility` },
      { name: "twitter:description", content: DESCRIPTION },
      ...ogImage(`insights-${SLUG}`, TITLE),
      ogUrl(`/insights/${SLUG}`),
    ],
    links: canonicalLinks(`/insights/${SLUG}`),
    scripts: [
      jsonLd(faqPageSchema(`/insights/${SLUG}`, postFaqs[SLUG], `${absoluteUrl(`/insights/${SLUG}`)}#article`)),
      jsonLd(breadcrumbSchema([{ name: "Insights", path: "/insights" }, { name: TITLE, path: `/insights/${SLUG}` }])),
      jsonLd(
        webPageSchema({
          path: `/insights/${SLUG}`,
          name: `${TITLE} | Ajility`,
          description: DESCRIPTION,
          dateModified: modifiedAt(SLUG),
          mainEntityId: `${absoluteUrl(`/insights/${SLUG}`)}#article`,
          image: `insights-${SLUG}`,
        }),
      ),
      jsonLd(
        articleSchema({
          title: TITLE,
          description: DESCRIPTION,
          slug: SLUG,
          datePublished: publishedAt(SLUG),
          dateModified: modifiedAt(SLUG),
          section: CATEGORY,
        }),
      ),
    ],
  }),
  component: The6mPriceReview,
});

function The6mPriceReview() {
  return (
    <div className="bg-background">
      {/* Hero */}
      <section className="relative gradient-navy text-frost">
        <div className="grid-lines absolute inset-0 opacity-20" />
        <div className="relative mx-auto max-w-3xl px-5 pb-16 pt-36 sm:px-8 sm:pt-44">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-frost/70 transition hover:text-frost"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All operating insights and case studies
          </Link>
          <span className="eyebrow on-dark mt-8 block">{CATEGORY}, {READ}</span>
          <h1 className="mt-4 font-serif text-4xl leading-[1.08] text-frost sm:text-5xl">
            {TITLE}
          </h1>
          <p className="mt-3 text-sm text-frost/70">{postDateByline(SLUG)}</p>
          <p className="mt-6 text-lg leading-relaxed text-frost/80">{DESCRIPTION}</p>
          <div className="mt-10 flex items-center gap-4 border-t border-white/10 pt-6 text-sm text-frost/70">
            <div className="grid h-10 w-10 place-items-center rounded-full bg-frost/10 font-serif text-frost">
              A
            </div>
            <div>
              By <Link to="/about/adam-ayoub" className="font-medium text-frost underline decoration-white/30 underline-offset-4 transition hover:decoration-frost">Adam Ayoub</Link>
              <br />
              <span className="text-frost/80">Fractional COO, Ajility</span>
            </div>
          </div>
        </div>
      </section>


      <PhotoBand src={priceListMarkupImg} alt="A printed workshop service price list on a counter with a hand marking up the labour rates in pen, an invoice and coffee cup beside it." />

      {/* Article */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <article className="article-body">
          <p>Most businesses have never tested their own prices.</p>
          <p>I ran the annual labour and service price review at a 249-store national retailer, earlier in my career, in-house. One review cycle. More than $6M in first-year revenue growth.</p>
          <p>Not from working harder. Not from more customers. From pricing the work the business was already doing at what it was actually worth.</p>
          <MarginWaterfall />
          <p>The uncomfortable part: most founder-led businesses between $3M and $30M are running prices set years ago, adjusted by gut feel, discounted by whoever answers the phone.</p>
          <p>You don't need a 249-store network to fix this. The method scales down. A structured review looks at the price architecture, the path from list price to the cash that actually lands, and the leakage in between, then sequences the changes so volume can be watched while they land.</p>
          <p>The method is now what Ajility does, for founder-led businesses, at founder-led prices, published on the site.</p>
          <p>If you haven't tested your prices in the last two years, that's the first conversation to have.</p>
          <p>
            <Link to="/specialisations/pricing-and-revenue-management" className="font-medium text-midnight underline decoration-border underline-offset-4 transition hover:decoration-midnight">
              See the pricing and revenue management specialisation
            </Link>
          </p>
          <p>For the full case, read <Link to="/insights/$slug" params={{ slug: "pricing-engine-kmart-tyre-auto" }} className="font-medium text-midnight underline decoration-border underline-offset-4 transition hover:decoration-midnight">{getPost("pricing-engine-kmart-tyre-auto")?.title}</Link>.</p>
          <ArticleFaqs faqs={postFaqs[SLUG]} />
        </article>
      </section>

      {/* CTA */}
      <section className="bg-midnight-deep">
        <div className="mx-auto max-w-4xl px-5 py-20 text-center text-frost sm:px-8">
          <h2 className="font-serif text-4xl leading-tight sm:text-5xl">
            Want to know where your real bottleneck is?
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-frost/75">
            Book a 30-minute fit call, a structured conversation rather than a pitch, about
            where the constraint actually sits in your business.
          </p>
          <BookFitCall
            placement={`insights_article_${SLUG}_footer`}
            buttonClassName="mt-8 inline-flex items-center gap-2 rounded-full bg-frost px-7 py-3.5 text-sm font-medium text-midnight-deep transition hover:bg-white"
            noteClassName="mt-2 text-sm font-medium text-frost/70 hover:text-frost"
          >
            Book a fit call <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </BookFitCall>
          <p className="mt-8 font-serif italic text-frost/80">
            "Strategy without execution is hallucination."
          </p>
        </div>
      </section>
    </div>
  );
}
